import { Button, Form, Input, InputNumberProps, message, Select, Slider, Typography } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from '../../../i18n/i18n';
import {
  Installation,
  SiphonDiameter,
  SiphonMaterial,
  SiphonType,
} from '../../../gen/proto/api/gateway/v1/installation_pb';
import { siphonDiameterLabel, siphonMaterialLabel, siphonTypeLabel } from '../../../utils/mapTranslationEnums';
import { installationApi } from '../../../api/installation.api';
import type { JsonObject } from '@bufbuild/protobuf';
import { EditOutlined } from '@ant-design/icons';
import ModalCustom from '../../utils/ModalCustom/ModalCustom';
import './EditInstallation.scss';

const { Text } = Typography;

type InstallationValues = {
  name: string;
  erpId: string;
  type: SiphonType;
  material: SiphonMaterial;
  diameter: SiphonDiameter;
  effluent: string;
  limit: number;
};

type EditInstallationProps = {
  uuid?: string;
  locationUuid?: string;
  onSuccess?: () => void;
  areaToEdit: string;
};

const EditInstallation = ({ uuid, locationUuid, onSuccess, areaToEdit }: EditInstallationProps) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [resolvedLocationUuid, setResolvedLocationUuid] = useState<string>('');
  const [resolvedOtherInfo, setResolvedOtherInfo] = useState<JsonObject | undefined>(undefined);
  const [resolvedInstallation, setResolvedInstallation] = useState<Installation | null>(null);
  const { t } = useTranslation('installation-location', { keyPrefix: 'installationLocationDetail.modal' });
  const [form] = Form.useForm<InstallationValues>();
  const siphonType = Form.useWatch('type', form);
  const nameString = 'name';
  const siphonString = 'siphon';

  const siphonTypeOptions = [
    { value: SiphonType.EFFLUENT, label: siphonTypeLabel(SiphonType.EFFLUENT) },
    { value: SiphonType.INFLUENT, label: siphonTypeLabel(SiphonType.INFLUENT) },
  ];

  const siphonMaterialOptions = [
    { value: SiphonMaterial.PVC, label: siphonMaterialLabel(SiphonMaterial.PVC) },
    { value: SiphonMaterial.HDPE, label: siphonMaterialLabel(SiphonMaterial.HDPE) },
    { value: SiphonMaterial.OTHER, label: siphonMaterialLabel(SiphonMaterial.OTHER) },
  ];

  const siphonDiameterOptions = [
    { value: SiphonDiameter.SIPHON_DIAMETER_90_MM, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_90_MM) },
    { value: SiphonDiameter.SIPHON_DIAMETER_110_MM, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_110_MM) },
    { value: SiphonDiameter.SIPHON_DIAMETER_160_MM, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_160_MM) },
    { value: SiphonDiameter.SIPHON_DIAMETER_200_MM, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_200_MM) },
    { value: SiphonDiameter.SIPHON_DIAMETER_240_MM, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_240_MM) },
    { value: SiphonDiameter.SIPHON_DIAMETER_OTHER, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_OTHER) },
  ];

  const onChange: InputNumberProps['onChange'] = (newValue) => {
    setInputValue(newValue as number);
  };

  const handleOpen = async () => {
    if (uuid) {
      try {
        const installation = await installationApi.getByUuid(uuid);
        const limit = installation.siphonCollapseLimitPercentage ?? 0;
        form.setFieldsValue({
          name: installation.name,
          erpId: installation.erpId,
          limit,
          type: installation.siphonType,
          material: installation.siphonMaterial,
          diameter: installation.siphonDiameterMm,
          effluent: installation.siphonEffluentSiphonUuid,
        });
        setInputValue(limit);
        setResolvedLocationUuid(installation.locationUuid);
        setResolvedOtherInfo(installation.otherInfo);
        setResolvedInstallation(installation);
      } catch (e) {
        messageApi.error(t('common:errors.generic'));
      }
    } else {
      form.resetFields();
      setInputValue(0);
      setResolvedLocationUuid(locationUuid ?? '');
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    let values: InstallationValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setLoading(true);
    try {
      if (uuid) {
        await installationApi.update(
          uuid,
          values.name ?? resolvedInstallation?.name ?? '',
          values.erpId ?? resolvedInstallation?.erpId ?? '',
          resolvedLocationUuid,
          values.type ?? resolvedInstallation?.siphonType ?? SiphonType.UNSPECIFIED,
          values.material ?? resolvedInstallation?.siphonMaterial ?? SiphonMaterial.UNSPECIFIED,
          values.diameter ?? resolvedInstallation?.siphonDiameterMm ?? SiphonDiameter.SIPHON_DIAMETER_UNSPECIFIED,
          values.effluent || resolvedInstallation?.siphonEffluentSiphonUuid || undefined,
          areaToEdit === siphonString ? inputValue : (resolvedInstallation?.siphonCollapseLimitPercentage ?? 0),
          resolvedOtherInfo,
          resolvedInstallation?.comment ?? '',
        );
      } else {
        await installationApi.create(
          values.name,
          values.erpId,
          values.type,
          values.material,
          values.diameter,
          values.effluent || undefined,
          inputValue,
          resolvedLocationUuid,
        );
      }
      setIsModalOpen(false);
      onSuccess?.();
    } catch {
      messageApi.error(t('common:errors.generic'));
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      {contextHolder}
      {areaToEdit === nameString && (
        <Button type={'text'} size={'middle'} onClick={handleOpen}>
          <EditOutlined className={'InstallationDetailModal__edit__button'} />
        </Button>
      )}
      {areaToEdit === siphonString && (
        <Button
          className={'EditInstallationModal__button'}
          type={'primary'}
          size={'middle'}
          onClick={handleOpen}
          icon={<EditOutlined />}
        >
          {t('edit_siphon')}
        </Button>
      )}

      <ModalCustom
        title={t('title_edit')}
        description={t('description')}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        footer={
          <Button onClick={handleSubmit} loading={loading}>
            {t('edit')}
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          className={'Modal__form'}
          onFinish={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.defaultPrevented) {
              e.preventDefault();
              form.submit();
            }
          }}
        >
          {areaToEdit === nameString && (
            <>
              <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="erpId" label={t('erpId')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </>
          )}
          {areaToEdit === siphonString && (
            <>
              <Form.Item name="type" label={t('siphonType')} rules={[{ required: true }]}>
                <Select options={siphonTypeOptions} />
              </Form.Item>
              <Form.Item name="material" label={t('material')} rules={[{ required: true }]}>
                <Select options={siphonMaterialOptions} />
              </Form.Item>
              <Form.Item name="diameter" label={t('diameter')} rules={[{ required: true }]}>
                <Select options={siphonDiameterOptions} />
              </Form.Item>
              {siphonType === SiphonType.INFLUENT && (
                <Form.Item name="effluent" label={t('effluent')} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              )}
              <Form.Item name="limit" label={t('siphonLimit')}>
                <div className={'Modal__slider'}>
                  <Slider className={'Modal__slider__bar'} value={inputValue} onChange={onChange} />
                  <Text className={'Modal__text'}>{inputValue}%</Text>
                </div>
              </Form.Item>
            </>
          )}
        </Form>
      </ModalCustom>
    </>
  );
};

export default EditInstallation;
